import { Button, UsageBar, Tooltip } from '@crazyegginc/hatch';

import { useMetric, useAuthContext, useNeedsUpgradeToAccessFeature, useManageSubscriptionPath } from '/src/hooks';
import { SectionHeader } from '../components/accountUI';

import { FEATURES } from '/src/features/_global/constants';

import { ReactComponent as LockIcon } from '@crazyegginc/hatch/dist/images/icon-lock-filled.svg';
import { AppLink } from '/src/features/_global/components/AppLink';

export function PlanDetails() {
  const { currentAccount, capabilities } = useAuthContext();
  const isStripeUser = Boolean(currentAccount?.useStripe);

  const storageDuration = capabilities.recordings.quotas.storageDuration.limit;

  const visitsLimit = capabilities.account.quotas.monthlyPageViews.limit;
  const visits = capabilities.account.quotas.monthlyPageViews.current;

  const recordingsUsed = capabilities.recordings.quotas.monthlyRecordings.current;
  const recordingsLimit = capabilities.recordings.quotas.monthlyRecordings.limit;
  const snapshotsUsed = capabilities.snapshots.quotas.totalSnapshots.current;
  const snapshotsLimit = capabilities.snapshots.quotas.totalSnapshots.limit;

  return (
    <>
      <SectionHeader className="mt-10">Plan Details and Usage</SectionHeader>
      <div className="flex w-full space-x-5">
        <section className="flex w-2/3 rounded-md border-2 border-mystic-500 px-5 py-[30px]">
          <div className="flex w-full space-x-5">
            <div className="w-1/2 flex-col space-y-4">
              <PlanDetailsRow
                title="Pageviews"
                locked={useNeedsUpgradeToAccessFeature(FEATURES.SNAPSHOTS)}
                value={`${(visitsLimit || 0).toLocaleString()} /month`}
                isStripeUser={isStripeUser}
              />
              <PlanDetailsRow
                title="Heatmaps"
                feature="Snapshots"
                locked={useNeedsUpgradeToAccessFeature(FEATURES.SNAPSHOTS)}
                value={(snapshotsLimit || 0).toLocaleString()}
                isStripeUser={isStripeUser}
              />
              <PlanDetailsRow
                title="Recordings"
                locked={useNeedsUpgradeToAccessFeature(FEATURES.RECORDINGS)}
                value={`${(recordingsLimit || 0).toLocaleString()} /month ${
                  storageDuration != null ? `(${storageDuration} months storage)` : ''
                }`}
                isStripeUser={isStripeUser}
              />
              <PlanDetailsRow title="Conversions" locked={useNeedsUpgradeToAccessFeature(FEATURES.GOALS)} />
              <PlanDetailsRow title="A/B Tests" locked={useNeedsUpgradeToAccessFeature(FEATURES.AB_TESTING)} />
              <PlanDetailsRow
                title="Traffic Analysis"
                locked={useNeedsUpgradeToAccessFeature(FEATURES.TRAFFIC_ANALYSIS)}
                value="based on recorded sessions"
                isStripeUser={isStripeUser}
              />
            </div>
            <div className="w-1/2 flex-col space-y-4">
              <PlanDetailsRow
                title="Errors Tracking"
                locked={useNeedsUpgradeToAccessFeature(FEATURES.ERRORS_TRACKING)}
                value="based on recorded sessions"
                isStripeUser={isStripeUser}
              />
              <PlanDetailsRow
                title="Surveys"
                locked={useNeedsUpgradeToAccessFeature(FEATURES.SURVEYS)}
                value="unlimited with unlimited responses"
                isStripeUser={isStripeUser}
              />
              <PlanDetailsRow
                title="CTAs"
                locked={useNeedsUpgradeToAccessFeature(FEATURES.CTAS)}
                isStripeUser={isStripeUser}
              />
              <PlanDetailsRow
                title="Team Members"
                locked={useNeedsUpgradeToAccessFeature(FEATURES.TEAM)}
                isStripeUser={isStripeUser}
              />
              <PlanDetailsRow title="Websites" isStripeUser={isStripeUser} />
            </div>
          </div>
        </section>

        <section className="relative flex w-1/3 flex-col justify-center space-y-5 rounded-md border-2 border-mystic-500 px-5 py-[30px]">
          {!useNeedsUpgradeToAccessFeature(FEATURES.SNAPSHOTS) ? (
            <FeatureUsageMeter feature="pageviews" used={visits} limit={visitsLimit} />
          ) : (
            <LockedUsageBar feature="pageviews" isStripeUser={isStripeUser} />
          )}

          {!useNeedsUpgradeToAccessFeature(FEATURES.SNAPSHOTS) ? (
            <FeatureUsageMeter feature="Heatmaps" used={snapshotsUsed} limit={snapshotsLimit} />
          ) : (
            <LockedUsageBar feature="Snapshots" title="Heatmaps" isStripeUser={isStripeUser} />
          )}

          {!useNeedsUpgradeToAccessFeature(FEATURES.RECORDINGS) ? (
            <FeatureUsageMeter feature="Recordings" used={recordingsUsed} limit={recordingsLimit} />
          ) : (
            <LockedUsageBar feature="Recordings" isStripeUser={isStripeUser} />
          )}
        </section>
      </div>
    </>
  );
}

function PlanDetailsRow({ title, value = 'unlimited', locked = false, isStripeUser = false, feature }) {
  const trackMetric = useMetric();
  const subscriptionPath = useManageSubscriptionPath();

  return (
    <div className="flex">
      <div className="mr-3 w-24 shrink-0 text-xs font-normal text-black-pearl-500 2xl:text-sm">{title}</div>
      {locked ? (
        <div className="flex items-center text-xs font-normal text-lynch-500 2xl:text-sm">
          <AppLink
            className="text-link leading-tight"
            to={subscriptionPath}
            onMouseDown={() =>
              trackMetric(
                'upgrade_cta_click',
                `plan_locked_usage_${(feature || title).toLowerCase().replace(/\/| /g, '_')}`,
              )
            }
            target={isStripeUser ? '_blank' : '_self'}
            rel={isStripeUser ? 'noopener noreferrer' : undefined}
          >
            upgrade to access
          </AppLink>
        </div>
      ) : (
        <div className="text-xs font-semibold text-black-pearl-500 2xl:text-sm">{value}</div>
      )}
    </div>
  );
}

function FeatureUsageMeter({ feature, used, limit }) {
  // If 0 divided by 0, will return NaN
  const usage = Math.min(Math.floor((used / limit) * 100), 100) || 0;

  return (
    <div className="text-body-2 w-full text-xs">
      <div className="mb-2.5 flex justify-between">
        <span>
          {usage}% of {feature} used
        </span>
        <span>
          {(used || 0).toLocaleString()} / {(limit || 0).toLocaleString()}
        </span>
      </div>
      <UsageBar usage={usage} size="lg" />
    </div>
  );
}

function LockedUsageBar({ feature, isStripeUser = false, title }) {
  const trackMetric = useMetric();
  const subscriptionPath = useManageSubscriptionPath();
  return (
    <div className="text-body-5 w-full text-xs">
      <Tooltip
        placement="bottom"
        arrowSkiddingPercent="15"
        skiddingPercent="20"
        interactive={true}
        tooltipContent={
          <div className="mb-2.5 ml-2 mr-5 mt-4 flex max-w-[230px] flex-col text-left">
            Upgrade to a paid plan to access {title || feature}.
            <Button
              size="xs"
              theme="dark"
              className="!mt-2.5 !self-start"
              component={AppLink}
              to={subscriptionPath}
              target={isStripeUser ? '_blank' : '_self'}
              onMouseDown={() => trackMetric('upgrade_cta_click', `plan_locked_usage_${feature.toLowerCase()}`)}
            >
              Upgrade Now
            </Button>
          </div>
        }
      >
        <div className="mb-2.5 flex items-center">
          <span className="mt-px">0% of {title || feature} used</span>
          <LockIcon className="ml-1.5 h-4 w-4 shrink-0 fill-current text-cadet-blue-500" />
        </div>
        <UsageBar usage={0} size="lg" />
      </Tooltip>
    </div>
  );
}
